import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  Image
} from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { authAPI, getDeviceInfo } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import { MessageAlert } from '../Common';

// Import assets
import Logo from '../../assets/logo.png';
import EmailIcon from '../../assets/email.svg';
import ChevronRight from '../../assets/chevron-right.svg';
import LoginBanner1 from '../../assets/loginban1.png';
import LoginBanner2 from '../../assets/loginban2.png';
import LoginBanner3 from '../../assets/loginban3.png';

const loginBanners = [LoginBanner1, LoginBanner2, LoginBanner3];

function ForgotPassword() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [formData, setFormData] = useState({
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();

  // Image slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % loginBanners.length);
    }, 4000); // 4 seconds
    return () => clearInterval(interval);
  }, []);

  // Set body class for legacy CSS compatibility
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
    return () => {
      document.body.classList.remove('dark-mode', 'light-mode');
    };
  }, [mode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await authAPI.forgotPassword(formData.email);

      if (data.status === 'success') {
        navigate('/forgot-password-otp', { state: { emailOrUsername: formData.email } });
      } else {
        setError(data.message || 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image 
                src={loginBanners[currentBanner]} 
                alt="Login Banner" 
                className="login-slider-img" 
                style={{ filter: 'brightness(1.25)' }}
                fluid
              />
            </div>
            <div className="login-left-overlay"></div>
          </Col>
          {/* Right Side: Forgot Password Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            <div className="login-form-wrapper w-100" style={{ maxWidth: '486px' }}>
              <div className="text-center mb-5" style={{ marginTop: '32px' }}>
                <Image 
                  src={Logo} 
                  alt="Complimate Logo" 
                  className="login-logo-main mb-4" 
                  style={{ height: '60px' }}
                />
              </div>
              <div className="mb-4 text-center">
                <h2 className="login-title" style={{ color: mode === 'dark' ? '#26c3c0' : '#009391' }}>Forgot Your Password?</h2>
                <div className="login-subtitle">Enter your registered email and we’ll send you a 6-digit code to reset your password.</div>
              </div>
              <Form onSubmit={handleSubmit}>
                <MessageAlert 
                  type="error" 
                  show={!!error}
                  message={error}
                />
                <MessageAlert 
                  type="success" 
                  show={!!success}
                  message={success}
                />
                <div className="mb-4 position-relative">
                  <span className="login-icon email-icon">
                    <Image src={EmailIcon} alt="Email Icon" width={20} height={20} />
                  </span>
                  <Form.Control 
                    type="text" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="login-input ps-5" 
                    placeholder="Enter Email Address" 
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-teal w-100 login-btn d-flex align-items-center justify-content-center mb-4"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                  <span className="ms-2">
                    <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                  </span>
                </Button>
                <div className="text-center">
                <span className="login-signup-text">Remember It ? 
                  <Link to="/login" className="login-forgot">
                    <span style={{ color: mode === 'dark' ? '#26c3c0' : '#009391', fontWeight: 'bold' }}> Signin</span>
                  </Link>
                  </span>
                </div>
              </Form>
              <div className="text-center mt-4">
                <span className="login-signup-text">
                  Don't have an account? <Link to="/signup" className="login-signup-link">Sign up here.</Link>
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ForgotPassword; 